import { Router } from "express";
import mongoose from "mongoose";
import { fail } from "../shared";
import { wrap } from "../shared/server";
import { Conversation, pairKeyFor } from "../models/Conversation";
import { Message } from "../models/Message";
import { directory, requireEndUser } from "../auth";

const router = Router();
router.use(requireEndUser);

const PAGE = 50;
const oid = (v: string) => new mongoose.Types.ObjectId(v);

/** THE WALL: you may only ever act on a conversation you are part of. */
async function myConversation(me: any, conversationId: string) {
  if (!mongoose.isValidObjectId(conversationId)) return null;
  const convo = await Conversation.findById(conversationId);
  if (!convo) return null;
  const mine = convo.participants.some((p) => String(p) === me.sub);
  // Say the same thing whether it does not exist or is not theirs.
  return mine ? convo : null;
}

/* ---------------------------- who can I talk to ---------------------------- */

// GET /api/chat/people — everyone on my domain, with unread counts
router.get("/people", wrap(async (req: any, res: any) => {
  let people: Awaited<ReturnType<typeof directory>>;
  try {
    people = await directory(req.me.domain);
  } catch {
    return fail(res, 503, "DIRECTORY_UNAVAILABLE", "The company directory could not be reached.");
  }

  const others = people.filter((p) => p.id !== req.me.sub);

  // one query for every direct conversation I am in
  const convos = await Conversation.find({ participants: oid(req.me.sub), type: "direct" }).lean();
  const byOther = new Map<string, any>();
  for (const c of convos) {
    const other = c.participants.map(String).find((p) => p !== req.me.sub);
    if (other) byOther.set(other, c);
  }

  const unread = await Message.aggregate([
    { $match: { conversationId: { $in: convos.map((c) => c._id) }, senderId: { $ne: oid(req.me.sub) }, readBy: { $ne: oid(req.me.sub) }, deletedAt: null } },
    { $group: { _id: "$conversationId", n: { $sum: 1 } } },
  ]);
  const unreadBy = new Map(unread.map((u) => [String(u._id), u.n]));

  res.json({
    me: { id: req.me.sub, address: req.me.address, displayName: req.me.displayName, avatarSeed: req.me.avatarSeed },
    people: others
      .map((p) => {
        const c = byOther.get(p.id);
        return {
          ...p,
          conversationId: c ? String(c._id) : null,
          lastMessageAt: c?.lastMessageAt ?? null,
          lastMessagePreview: c?.lastMessagePreview ?? "",
          unread: c ? unreadBy.get(String(c._id)) ?? 0 : 0,
        };
      })
      .sort((a, b) => {
        if (a.unread !== b.unread) return b.unread - a.unread;
        const at = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const bt = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        if (at !== bt) return bt - at;
        return a.displayName.localeCompare(b.displayName);
      }),
  });
}));

/* ------------------------------ conversations ------------------------------ */

/**
 * POST /api/chat/conversations  { withUserId }
 * Opens the chat with someone, creating it the first time. Idempotent — asking
 * twice returns the same conversation, thanks to the unique pair key.
 */
router.post("/conversations", wrap(async (req: any, res: any) => {
  const withUserId = String(req.body?.withUserId ?? "");
  if (!mongoose.isValidObjectId(withUserId)) return fail(res, 400, "BAD_REQUEST", "Choose someone to chat with.");
  if (withUserId === req.me.sub) return fail(res, 400, "SELF", "You cannot start a chat with yourself.");

  // They must be on YOUR domain — checked against the directory, not trusted from the request.
  let people: Awaited<ReturnType<typeof directory>>;
  try {
    people = await directory(req.me.domain);
  } catch {
    return fail(res, 503, "DIRECTORY_UNAVAILABLE", "The company directory could not be reached.");
  }
  const other = people.find((p) => p.id === withUserId);
  if (!other) return fail(res, 404, "NOT_FOUND", "That person is not available to you.");

  const key = pairKeyFor(req.me.sub, withUserId);
  let convo = await Conversation.findOne({ pairKey: key });
  if (!convo) {
    try {
      convo = await Conversation.create({
        type: "direct",
        participants: [oid(req.me.sub), oid(withUserId)].sort((a, b) => String(a).localeCompare(String(b))),
        pairKey: key,
        domain: req.me.domain,
        clientId: req.me.clientId ? oid(req.me.clientId) : null,
      });
    } catch (err: any) {
      // two tabs opened it at once — the unique index did its job
      if (err?.code === 11000) convo = await Conversation.findOne({ pairKey: key });
      else throw err;
    }
  }

  res.json({
    conversation: { id: String(convo!._id), type: convo!.type, with: other },
  });
}));

/* -------------------------------- messages -------------------------------- */

// GET /api/chat/messages?conversationId=&before=
router.get("/messages", wrap(async (req: any, res: any) => {
  const convo = await myConversation(req.me, String(req.query.conversationId ?? ""));
  if (!convo) return fail(res, 404, "NOT_FOUND", "That conversation is not available to you.");

  const filter: any = { conversationId: convo._id, deletedAt: null };
  if (req.query.before && mongoose.isValidObjectId(String(req.query.before))) {
    filter._id = { $lt: oid(String(req.query.before)) };
  }

  const rows = await Message.find(filter).sort({ sentAt: -1 }).limit(PAGE).lean();
  res.json({
    conversationId: String(convo._id),
    messages: rows.reverse().map((m) => ({
      id: String(m._id),
      senderId: String(m.senderId),
      senderName: m.senderName,
      senderAddress: m.senderAddress,
      body: m.body,
      sentAt: m.sentAt,
      mine: String(m.senderId) === req.me.sub,
      readByOther: (m.readBy ?? []).some((r) => String(r) !== req.me.sub),
    })),
    hasMore: rows.length === PAGE,
  });
}));

// POST /api/chat/messages  { conversationId, body }
router.post("/messages", wrap(async (req: any, res: any) => {
  const convo = await myConversation(req.me, String(req.body?.conversationId ?? ""));
  if (!convo) return fail(res, 404, "NOT_FOUND", "That conversation is not available to you.");

  const body = String(req.body?.body ?? "").trim();
  if (!body) return fail(res, 400, "EMPTY", "Type something to send.");
  if (body.length > 8000) return fail(res, 400, "TOO_LONG", "That message is too long. Keep it under 8000 characters.");

  const message = await Message.create({
    conversationId: convo._id,
    senderId: oid(req.me.sub),
    senderAddress: req.me.address,
    senderName: req.me.displayName, // snapshot, on purpose
    body,
    readBy: [oid(req.me.sub)], // your own message is read by you
  });

  convo.lastMessageAt = message.sentAt;
  convo.lastMessagePreview = body.slice(0, 120);
  convo.lastMessageBy = oid(req.me.sub);
  await convo.save();

  res.status(201).json({
    message: {
      id: String(message._id),
      senderId: req.me.sub,
      senderName: req.me.displayName,
      senderAddress: req.me.address,
      body: message.body,
      sentAt: message.sentAt,
      mine: true,
      readByOther: false,
    },
  });
}));

// POST /api/chat/read  { conversationId }
router.post("/read", wrap(async (req: any, res: any) => {
  const convo = await myConversation(req.me, String(req.body?.conversationId ?? ""));
  if (!convo) return fail(res, 404, "NOT_FOUND", "That conversation is not available to you.");

  const result = await Message.updateMany(
    { conversationId: convo._id, senderId: { $ne: oid(req.me.sub) }, readBy: { $ne: oid(req.me.sub) } },
    { $addToSet: { readBy: oid(req.me.sub) } }
  );
  res.json({ ok: true, marked: result.modifiedCount });
}));

/**
 * GET /api/chat/unread — one small number for the module rail badge.
 * Called on a timer, so it is deliberately cheap.
 */
router.get("/unread", wrap(async (req: any, res: any) => {
  const convos = await Conversation.find({ participants: oid(req.me.sub) }).select("_id").lean();
  const total = await Message.countDocuments({
    conversationId: { $in: convos.map((c) => c._id) },
    senderId: { $ne: oid(req.me.sub) },
    readBy: { $ne: oid(req.me.sub) },
    deletedAt: null,
  });
  res.json({ unread: total });
}));

export default router;
